<template>
  <div class="so-container"
    :class="[ isFirstLevelContainer ? 'top-level' : 'sub-level']"
    data-structure="container">

    <!-- Container header -->
    <div class="so-container__head-outer" @click="toggle" v-if="!isFirstLevelContainer">
      <span v-if="this.container.connector == 'OR'" class="so-container__connector">OR</span>
      <span v-if="this.container.connector == 'AND'" class="so-container__connector">AND</span>

      <button type="button" class="so-container__head" :class="['so-container__head', show ? 'expanded' : '']">
        <div class="so-container__head-left">
          <i class="far fa-chevron-down icon-spot-light"></i>
          <span class="so-container__head-title">{{container.title}}</span>
          <span class="so-container__head-helptext" v-if="!show">Show all</span>
          <span class="so-container__head-helptext" v-if="show">Hide unselected</span>
        </div>
        <div class="so-container__head-right text-right">
          <div class="so-container__head-alert"
            :class="[ credits > 0 && credits < container.credits ? 'warning' : '', credits > 0 && credits > container.credits ? 'danger' : '' ]">
            <span v-if="credits > 0 && credits < container.credits">
              <i class="far fa-exclamation-circle"></i>Select {{ container.credits - credits }} more points
            </span>
            <span v-if="credits > 0 && credits > container.credits">
              <i class="far fa-exclamation-circle"></i>Too many units selected
            </span>
            <span class="so-container__head-credit-count" :class="credits > 0 ? 'highlight' : ''">{{credits}} / {{container.credits}} points</span>
          </div>
        </div>
      </button>

      <div class="slide-out__body-description" v-if="container.description.length">{{ container.description }}</div>

    </div>

    <!-- If a container has NO mandatory units but SOME optional units (as direct children) -->
    <transition name="fade">
      <!-- Container contents... -->
      <div :class="['so-container__body', !isFirstLevelContainer && isEmptyContainer ? 'contains-children' : 'contains-no-children', showAll ? 'show' : '']">

        <!-- If not condition set show regular list -->
        <ul v-if="relationships.length && !conditionSets.length">
          <li v-for="relationship in relationships" :key="relationship.id">
            <SlideOutRelationship :relationship="relationship" :location="location+'>'+relationship.code"
              :mandatory="checkMandatory(relationship)" :parentLocation="location" :firstLevelTitle="firstLevelTitle" :showAll="showAll"
              :verticalGrouping="verticalGrouping" />
          </li>
        </ul>

        <!-- If condition set show search / selected view toggle -->
        <div class="so-cset__toggle row" v-if="conditionSets.length">
          <div class="col text-right">
            <transition name="fade">
              <button type="button" class="btn-link-teal-sm" v-if="viewMode == 'selected'" v-on:click="toggleView('search')">
                <i class="far fa-chevron-left mr-1"></i>Back to unit search
              </button>
              <button type="button" class="btn-link-teal-sm" v-if="viewMode == 'search' && credits > 0" v-on:click="toggleView('selected')">
                Show selected units
              </button>
            </transition>
          </div>
        </div>

        <transition name="fade" mode="out-in">

          <!-- Selection of Units (Relationships) -->
          <template v-if="viewMode == 'selected'">
            <ul v-if="relationships.length">
              <li v-for="relationship in relationships" :key="relationship.id">
                <SlideOutRelationship :relationship="relationship" :location="location+'>'+relationship.code"
                  :mandatory="checkMandatory(relationship)" :parentLocation="location" :firstLevelTitle="firstLevelTitle" :showAll="showAll"
                  :verticalGrouping="verticalGrouping" />
              </li>
            </ul>
            <template v-if="credits < 1">
              <div class="page-content-state">
                <span class="page-content-state__icon  fal fa-folder-open"></span>
                <div class="page-content-state__content">
                  You have no selected units for this area<br />
                  <button type="button" class="btn btn-primary mt-3" v-on:click="toggleView('search')">Add units</button>
                </div>
              </div>
            </template>
          </template>

          <!-- Searching of Units (Condition Sets) -->
          <div v-if="show && conditionSets.length && viewMode == 'search'" class="str-rels">
            <SlideOutConditionSet v-for="mc in conditionSets" :key="mc.description" :data="mc" :requiredCredits="parseInt(container.credits)"
              :location="location" :mandatory="checkMandatory(mc)" :firstLevelTitle="firstLevelTitle" :verticalGrouping="verticalGrouping" :containerId="container.cl_id" />
          </div>

        </transition>

        <!-- Container  -->
        <ul v-if="show && containers.length">
          <li v-for="(container, index) in containers" :key="container.title+'-'+container.order" :id="`anchor-${electiveContainerCode}-${index}`">
            <SlideOutContainer :container="container" :location="location+'>'+container.title+'-'+container.order"
              :mandatory="checkMandatory(container)" :firstLevelTitle="firstLevelTitle" :shownByDefault="isEmptyContainer"
              :verticalGrouping="verticalGrouping" />
          </li>
        </ul>

      </div>
    </transition>

  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { dynamicSort } from 'common/helpers/dynamic-sort.js'
import creditChangeCalculator from 'client/js/utils/credit-change-calculator.js'

import SlideOutRelationship from './SlideOutRelationship.vue'
import SlideOutConditionSet from './SlideOutConditionSet.vue'

export default {
  name: 'SlideOutContainer',
  data: function () {
    return {
      credits: 0,
      show: true,
      showAll: true,
      viewMode: 'search'
    }
  },
  props: {
    container: {
      type: Object,
      required: true
    },
    location: {
      type: String,
      required: true
    },
    mandatory: {
      type: Boolean,
      required: true
    },
    firstLevelTitle: {
      type: String,
      required: true
    },
    isFirstLevelContainer: {
      type: Boolean,
      required: false,
      default: false
    },
    shownByDefault: {
      type: Boolean,
      required: false,
      default: false
    },
    electiveContainerCode: {
      type: String,
      required: false
    }
  },
  mounted: function () {
    //update the toggle ability if selected elements are manditory or optional
    if (this.mandatoryContainers.length > 0 || this.mandatoryRelationships.length > 0) {
      this.show = true
    } else {
      this.show = false
    }

    if (this.isFirstLevelContainer || this.shownByDefault) this.show = true

    // update credits based on transaction log
    this.credits = this.totalCredits(this.location)

    // subscribe to add credit event mutations
    this.unsubscribeCreditEvents = this.$store.subscribeAction((action, state) => {
      this.credits = creditChangeCalculator(action.type, action.payload, this.location, this.credits)
    })
  },
  computed: {
    isEmptyContainer: function () {
      return (
        this.mandatoryRelationships?.length == 0 &&
        this.optionalRelationships?.length == 0 &&
        this.mandatoryConditionSets?.length == 0 &&
        this.optionConditionSets?.length == 0
      )
    },
    containers: function () {
      return this.container.container.sort(dynamicSort('order'))
    },
    relationships: function () {
      return this.container.relationship.sort(dynamicSort('order'))
    },
    conditionSets: function () {
      return this.container.conditionSet.sort(dynamicSort('order'))
    },
    mandatoryContainers: function () {
      return this.containers.filter((x) => x.connector === 'AND')
    },
    optionalContainers: function () {
      return this.containers.filter((x) => x.connector === 'OR')
    },
    mandatoryRelationships: function () {
      return this.relationships.filter((x) => x.connector === 'AND')
    },
    optionalRelationships: function () {
      return this.relationships.filter((x) => x.connector === 'OR')
    },
    mandatoryConditionSets: function () {
      return this.container.conditionSet.filter((x) => x.connector === 'AND')
    },
    optionConditionSets: function () {
      return this.container.conditionSet.filter((x) => x.connector === 'OR')
    },
    verticalGrouping: function () {
      return this.container.verticalGrouping ?? ''
    },
    ...mapGetters({
      totalCredits: 'units/getTotalCredits'
    })
  },
  methods: {
    updateCredits(c) {
      this.credits = this.credits + c
    },
    checkMandatory(data) {
      if (this.mandatory && data.connector == 'AND') {
        return true
      } else {
        return false
      }
    },
    toggle() {
      this.show = !this.show
      this.showAll = !this.showAll
    },
    toggleView(view) {
      this.viewMode = view
    }
  },
  watch: {},
  beforeDestroy() {
    this.unsubscribeCreditEvents()
  },
  components: { SlideOutRelationship, SlideOutConditionSet }
}
</script>

